/**
 * @file PickingTaskItemsPanel.tsx
 * @description Левая колонка задания на сборку: счётчик «ПОЗИЦИИ (N)»,
 * таблица строк на `lg` и карточки на `<lg` (phase19.23; раскладка «как в
 * приёмке» — phase19.24).
 *
 * Поле скана отсюда уехало в панель отметки (`PickMarkPanel`): одно место
 * ввода на экране, как в приёмке. Панель по-прежнему ничего не знает про
 * запросы — получает готовые строки, фильтр и обработчики.
 */

import type { ReactNode } from "react";

import { PickItemsList } from "./PickItemsList";
import { PickItemsTable } from "./PickItemsTable";
import { SectionLabel } from "../../ui/SectionLabel";
import type {
  PickingItemsSource,
  PickingTaskItemDto,
} from "../../../lib/api-picking";

interface Props {
  /** Строки после фильтра поиска. */
  items: PickingTaskItemDto[];
  /** Всего строк в задании — счётчик «Позиции (N)» и «Показано N из M». */
  totalCount: number;
  itemsSource?: PickingItemsSource;
  /** Действие секции справа от счётчика: «Лист комплектации». */
  sectionAction?: ReactNode;
  /**
   * Поле поиска над списком. Нужно закрытому и чужому заданию: правой панели
   * у них нет (правило §5.2), а искать по длинному списку всё равно надо.
   */
  searchSlot?: ReactNode;
  /** Кнопка «Пересобрать задание» для полосы «строки потерялись». */
  rebuildAction?: ReactNode;
  /** Текущий запрос поиска. */
  query: string;
  onResetFilter: () => void;
  /** Задание живое и своё: позиции можно отмечать. */
  editable: boolean;
  busy: boolean;
  scannedItemId: string | null;
  onChangeQty: (itemId: string, qty: number) => void;
  onOpenManualQty: (itemId: string) => void;
  onOpenShortage: (itemId: string) => void;
  /** Снять отметку недостачи со строки (`shortageQty: 0` на сервере). */
  onClearShortage: (itemId: string) => void;
  /** Вернуть фокус в поле скана после тапа по списку и кнопок ±. */
  onReturnFocus: () => void;
}

export function PickingTaskItemsPanel({
  items,
  totalCount,
  itemsSource,
  sectionAction,
  searchSlot,
  rebuildAction,
  query,
  onResetFilter,
  editable,
  busy,
  scannedItemId,
  onChangeQty,
  onOpenManualQty,
  onOpenShortage,
  onClearShortage,
  onReturnFocus,
}: Props) {
  // Пустое задание, ошибка фильтра и подписи причин — забота `PickItemsList`:
  // таблица рисуется только когда строки есть, иначе на 1280 висела бы шапка
  // колонок над пустотой.
  const hasRows = items.length > 0;
  return (
    <section className="flex min-w-0 flex-col gap-2">
      {searchSlot}
      {/* Прогресс тут не дублируем: на desktop он в панели отметки и плитке
          «Собрано», на телефоне — в sticky-панели снизу. */}
      <SectionLabel as="h2" count={totalCount} trailing={sectionAction}>
        Позиции
      </SectionLabel>

      {hasRows && query.trim() ? (
        // Счётчик на фоне кабинета — основным цветом (SC69).
        <p className="text-xs text-on-surface" aria-live="polite">
          Показано {items.length} из {totalCount}
        </p>
      ) : null}

      {hasRows ? (
        <PickItemsTable
          items={items}
          editable={editable}
          busy={busy}
          scannedItemId={scannedItemId}
          onOpenManualQty={onOpenManualQty}
          onOpenShortage={onOpenShortage}
          onClearShortage={onClearShortage}
        />
      ) : null}

      <div className={hasRows ? "lg:hidden" : undefined}>
        <PickItemsList
          items={items}
          query={query}
          onResetFilter={onResetFilter}
          itemsSource={itemsSource}
          rebuildAction={rebuildAction}
          editable={editable}
          busy={busy}
          scannedItemId={scannedItemId}
          onChangeQty={onChangeQty}
          onOpenManualQty={onOpenManualQty}
          onOpenShortage={onOpenShortage}
          onClearShortage={onClearShortage}
          onReturnFocus={onReturnFocus}
        />
      </div>
    </section>
  );
}
